import { toRealCoordinates } from "utils/coordinates";
import { generateSlug } from "utils/getTripShareUrl";
import { EXPEDIA_TRACKING_PATH, wrapExpediaUrl } from "utils/affiliates/expediaUrl";
import { wrapHostelworldUrl } from "utils/affiliates/hostelworldUrl";

type TripLike = { _id?: string | null; name?: string | null } | null | undefined;

/**
 * Normalize arbitrary text (e.g. a country/place name) into a URL-safe slug:
 * lowercase, strip accents, drop non-alphanumeric characters, collapse
 * whitespace into single hyphens.
 */
export function slugify(text: string): string {
	return text
		.toLowerCase()
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.replace(/[^a-z0-9\s-]/g, "")
		.trim()
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-")
		.slice(0, 200);
}

const tripUrlSegment = (trip: TripLike): string => {
	if (trip?._id) {
		const slug = generateSlug(trip?.name || "");
		return `${trip._id}${slug ? `-${slug}` : ""}`;
	}
	return trip?.name ? encodeURIComponent(trip.name) : "";
};

/**
 * Build a `/trip/...` path for a trip, id-first (`/trip/<id>-<slug>`) so that two
 * trips sharing the same name never resolve to each other's data — the route's
 * `:name` param is parsed back into a tripId via `extractTripId` (see `useGraphTrip.ts`).
 * Falls back to the (encoded) name only when the trip has no `_id` yet (e.g. still creating).
 * Optionally appends a sub-path (e.g. "settings", `${destSlug}/accommodations`).
 * Returns "/" if the trip has neither `_id` nor `name`.
 */
export const buildTripPath = (trip: TripLike, subPath?: string): string => {
	const segment = tripUrlSegment(trip);
	if (!segment) return "/";
	const cleanSubPath = subPath ? subPath.replace(/^\/+/, "") : "";
	return cleanSubPath ? `/trip/${segment}/${cleanSubPath}` : `/trip/${segment}`;
};

/**
 * For components that may or may not have a `match.params.name` (id-slug or plain
 * name) already in scope. Reuses that param as-is when present — cheap, and it
 * preserves whichever format the URL already had — and only builds from the
 * loaded trip (id-first, via `buildTripPath`) when there's no route param to reuse
 * (e.g. a card/list component rendered outside the `/trip/:name` router tree).
 */
export const buildTripPathFromParam = (nameParam: string | undefined, trip: TripLike, subPath?: string): string => {
	if (nameParam) {
		const cleanSubPath = subPath ? subPath.replace(/^\/+/, "") : "";
		return cleanSubPath ? `/trip/${nameParam}/${cleanSubPath}` : `/trip/${nameParam}`;
	}
	return buildTripPath(trip, subPath);
};

/**
 * Trim and prepend `https://` if the input has no URL scheme.
 * Pass-through for inputs that already have any scheme (https, http, mailto, tel, etc.).
 * Returns "" for empty / whitespace-only / nullish input.
 */
export const ensureUrlScheme = (raw: string | undefined | null): string => {
	const trimmed = (raw ?? "").trim();
	if (!trimmed) return "";
	// RFC-3986 scheme: letter, then letters/digits/+/-/. then ":"
	if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
	return `https://${trimmed}`;
};

/**
 * Extract the simple domain from a URL (just hostname without path/query/protocol)
 * @param url - The original URL
 * @returns Simple domain (e.g., "example.com" from "https://www.example.com/path?query=1")
 */
export const getSimpleDomain = (url: string): string => {
	if (!url) return url;

	try {
		const parsed = new URL(ensureUrlScheme(url) || url);
		// Remove 'www.' prefix if present for cleaner display
		return parsed.hostname.replace(/^www\./, "");
	} catch {
		// Fallback: try to extract domain manually
		const withoutProtocol = url.replace(/^https?:\/\//, "").replace(/^www\./, "");
		const domainEnd = withoutProtocol.indexOf("/");
		return domainEnd > -1 ? withoutProtocol.substring(0, domainEnd) : withoutProtocol.split("?")[0];
	}
};

export const addOrReplaceParam = (url: string, key: string, value: string): string => {
	try {
		const u = new URL(url);
		u.searchParams.set(key, value);
		return u.toString();
	} catch {
		return url;
	}
};

/**
 * Which in-plan thing a clickout came from. All optional — pass whatever is in scope at the call
 * site. Without these the Booking.com partner report can only tell us WHICH USER booked, never
 * WHICH accommodation, which is why the affiliate capture gap is measurable in total and
 * undiagnosable in detail. Mirrors stippl-app/src/utils/urlUtils.ts.
 */
export type LabelContext = {
	tripId?: string;
	destinationId?: string;
	placeId?: string;
};

// Booking.com appends its own `-<token>@<epoch>` segments to whatever label we send, so our
// delimiter must not be `-` or `@`. `~` is unreserved in RFC 3986 and left literal by
// encodeURIComponent, so it survives the round-trip and keeps the label parseable.
const LABEL_SEP = "~";

// Ids are Mongo ObjectIds; strip anything else so a stray character can't break the delimiter.
const labelId = (v?: string): string | undefined => {
	const clean = (v || "").replace(/[^A-Za-z0-9]/g, "");
	return clean || undefined;
};

/**
 * Build the attribution label: `<username>~t<tripId>~d<destinationId>~p<placeId>`, omitting any
 * part that isn't available. Username stays FIRST so the stippl-admin CSV import, which matches
 * the leading segment against `UserProfile.username`, keeps working unchanged.
 */
export const buildAttributionLabel = (username?: string, ctx?: LabelContext): string | undefined => {
	if (!username) return undefined;
	const parts = [username];
	const trip = labelId(ctx?.tripId);
	const dest = labelId(ctx?.destinationId);
	const place = labelId(ctx?.placeId);
	if (trip) parts.push(`t${trip}`);
	if (dest) parts.push(`d${dest}`);
	if (place) parts.push(`p${place}`);
	return parts.join(LABEL_SEP);
};

// Partner ids/params. Keep these as the only place a raw affiliate credential is written —
// every helper below stamps by referencing these, never by re-typing the value.
const BOOKING_AID = "1787423";
const GYG_PARTNER_ID = "Y1LJHJX";
const VIATOR_PID = "P00050359";
const VIATOR_MCID = "42383";

/**
 * Parse a possibly scheme-less, possibly malformed string into a URL. Never throws — returns
 * null for anything `new URL()` rejects, which callers treat as "not a URL we can stamp".
 */
const parseUrlOrNull = (raw: string): URL | null => {
	try {
		return new URL(ensureUrlScheme(raw));
	} catch {
		return null;
	}
};

// ASCII-lowercase (the WHATWG URL parser already does this for the hostname itself, but we
// compare against literal domain strings below, so make the comparison side lowercase too) and
// strip a leading "www." or mobile "m." subdomain, so "WWW.Booking.COM" and "m.booking.com" match
// the same rule as "booking.com".
const normalizeHost = (hostname: string): string => hostname.toLowerCase().replace(/^(www|m)\./, "");

// `host === domain` covers the bare domain; `.endsWith(".<domain>")` covers every other
// subdomain (secure.booking.com, checkout.booking.com, …) without matching a lookalike like
// "notbooking.com".
const isSameOrSubdomain = (host: string, domain: string): boolean => host === domain || host.endsWith(`.${domain}`);

const isBookingHost = (host: string): boolean => isSameOrSubdomain(host, "booking.com");

/**
 * The single place a partner URL gets OUR affiliate credential. Everything else in this file
 * (formatBookingUrl's label, formatPlaceUrl, buildHotelBookingUrl, …) builds on this rather than
 * re-implementing per-partner matching.
 *
 * - Hostname comparison is lower-cased and subdomain-aware (`WWW.Booking.COM`, `secure.booking.com`).
 * - GetYourGuide matches any localized TLD (getyourguide.de, getyourguide.co.uk, …), not just `.com`.
 * - Params are set via `URLSearchParams`, so a `#fragment` is preserved and a foreign id (or a
 *   lookalike param like `said=`) is never touched except the exact key we own.
 * - Hostelworld/Expedia have no query-param id — we wrap the resolved URL in the SAME Partnerize
 *   prefix already used elsewhere in the frontend for those two partners (never invent a format).
 * - Scheme-less input (`www.booking.com/...`) is treated as `https://`. Unparseable input, and any
 *   URL whose host isn't one of these partners, is returned UNCHANGED, byte-for-byte.
 */
export const stampAffiliateUrl = (url: string): string => {
	const trimmed = (url ?? "").trim();
	if (!trimmed) return url;

	const parsed = parseUrlOrNull(trimmed);
	if (!parsed) return url;

	const host = normalizeHost(parsed.hostname);

	if (isBookingHost(host)) {
		// `ucfs=1` is a Booking.com hotel-page-only flag, unrelated to affiliate attribution —
		// preserved from the previous implementation, only for URLs that already carried a query.
		// A stored /hotel/ page with a query is cut back to our aid + ucfs=1, as before and as on
		// mobile (stippl-app urlUtils.ts): the rest of a stored query is someone else's session.
		if (parsed.search.length > 0 && parsed.pathname.includes("/hotel/")) {
			parsed.search = "";
			parsed.searchParams.set("aid", BOOKING_AID);
			parsed.searchParams.set("ucfs", "1");
			return parsed.toString();
		}
		parsed.searchParams.set("aid", BOOKING_AID);
		return parsed.toString();
	}

	if (host.startsWith("getyourguide.")) {
		parsed.searchParams.set("partner_id", GYG_PARTNER_ID);
		return parsed.toString();
	}

	if (isSameOrSubdomain(host, "viator.com")) {
		parsed.searchParams.set("pid", VIATOR_PID);
		parsed.searchParams.set("mcid", VIATOR_MCID);
		return parsed.toString();
	}

	if (isSameOrSubdomain(host, "hostelworld.com")) {
		return wrapHostelworldUrl(parsed.toString());
	}

	if (isSameOrSubdomain(host, "expedia.com")) {
		// Our tracking link lives on expedia.com itself, so it must not be wrapped a second time
		// (covers the `/otuid:1/` variant Partnerize's redirect inserts); another camref still gets wrapped.
		if (parsed.pathname.startsWith(EXPEDIA_TRACKING_PATH)) return url;
		return wrapExpediaUrl(parsed.toString());
	}

	// Not a partner we stamp (includes Klook and TripAdvisor — no tracking id/param exists
	// anywhere in the codebase for either, so we don't invent one) — unchanged, byte-for-byte.
	return url;
};

/**
 * Format a booking.com URL to ensure it includes the correct affiliate ID
 * @param url - The original URL
 * @param username - The current user's username. When provided (and the URL doesn't already
 *   carry a `label` param), stamps `&label=<username>` — the per-user attribution key that the
 *   stippl-admin Booking.com CSV import matches against `UserProfile.username` (exact,
 *   case-sensitive). Omitted for anonymous users so we never emit the literal `label=undefined`.
 * @param ctx - Optional trip/destination/place ids, so the partner report can be joined back to
 *   the in-plan accommodation that produced the clickout.
 * @returns URL with affiliate ID aid=1787423 (and, when applicable, the attribution label)
 */
export const formatBookingUrl = (url: string, username?: string, ctx?: LabelContext): string => {
	if (!url) return url;

	const parsedInput = parseUrlOrNull(url);
	if (!parsedInput || !isBookingHost(normalizeHost(parsedInput.hostname))) return url;

	// stampAffiliateUrl re-parses, but it's the single source of truth for the aid/ucfs shape —
	// worth the second parse to avoid two copies of that logic.
	const stamped = stampAffiliateUrl(url);
	const parsed = parseUrlOrNull(stamped) ?? parsedInput;

	// Always attribute the CURRENT clickout. A stored or shared URL can carry someone else's label,
	// and Booking.com would then credit them for this user's booking — including the loyalty points
	// that follow a stay. So strip any inherited label first, then re-stamp with whoever is
	// clicking now.
	//
	// Appended manually via `encodeURIComponent`, not `searchParams.set` — URLSearchParams
	// serializes a space as "+" and a "~" (the label's own delimiter, see LABEL_SEP above) as
	// "%7E", neither of which match the escaping the stippl-admin CSV import and this file's own
	// tests expect (`label=a%20b`, a literal "~" between segments).
	parsed.searchParams.delete("label");
	// The label goes into the query, before any #fragment, or Booking.com never receives it.
	const hash = parsed.hash;
	parsed.hash = "";
	const base = parsed.toString();
	const label = buildAttributionLabel(username, ctx);
	if (!label) return base + hash;
	return `${base}${base.includes("?") ? "&" : "?"}label=${encodeURIComponent(label)}${hash}`;
};

/**
 * Format a resolved Booking.com hotel-page URL for a specific stay: applies the
 * correct affiliate ID (via formatBookingUrl) and appends checkin/checkout dates.
 * @param url - The resolved hotel-page URL (e.g. from the bookingComLink GraphQL query)
 * @param checkIn - Check-in date (YYYY-MM-DD)
 * @param checkOut - Check-out date (YYYY-MM-DD)
 * @param username - Current user's username, threaded through to formatBookingUrl for the
 *   per-user attribution label.
 */
export const formatBookingHotelUrl = (url: string, checkIn?: string, checkOut?: string, username?: string, ctx?: LabelContext): string => {
	let result = formatBookingUrl(url, username, ctx);
	if (checkIn) result = addOrReplaceParam(result, "checkin", checkIn);
	if (checkOut) result = addOrReplaceParam(result, "checkout", checkOut);
	return result;
};

/**
 * Build the same-origin redirect-interstitial URL for a "Compare on Booking.com" click
 * (see BookingComRedirectPage.tsx). Resolving the hotel's real Booking.com deep link only
 * happens on click-through, not here — this just carries enough identity/geo/dates for
 * that page to resolve it. Coordinates that are missing or 0,0 are left out of the URL
 * (never written as lat=0&lng=0), which sends the page straight to its name + city search.
 */
export const buildBookingComRedirectUrl = (params: {
	hotelId: string | number;
	name: string;
	city?: string;
	latitude?: number | string | null;
	longitude?: number | string | null;
	checkIn?: string;
	checkOut?: string;
}): string => {
	const coords = toRealCoordinates(params.latitude, params.longitude);
	return `/bookings/redirect/booking-com?${new URLSearchParams({
		hotelId: String(params.hotelId),
		name: params.name,
		city: params.city ?? "",
		...(coords && { lat: String(coords.latitude), lng: String(coords.longitude) }),
		checkIn: params.checkIn ?? "",
		checkOut: params.checkOut ?? "",
	}).toString()}`;
};

/**
 * Format any place URL to ensure affiliate IDs are correct: booking.com (+ per-user attribution
 * label, via formatBookingUrl), getyourguide.com (any localized TLD), viator.com, and — via the
 * existing Partnerize wrapper(s), not a new tracking format — hostelworld.com and expedia.com.
 * Everything else (including tripadvisor.com and Klook, which have no id anywhere in the
 * codebase) is returned unchanged. See `stampAffiliateUrl` for the per-partner matching.
 * @param url - The original URL
 * @param username - Current user's username, threaded through to formatBookingUrl for the
 *   booking.com branch (per-user attribution label). Ignored by other partner branches.
 * @returns URL with appropriate affiliate IDs
 */
export const formatPlaceUrl = (url: string, username?: string, ctx?: LabelContext): string => {
	if (!url) return url;

	const parsed = parseUrlOrNull(url);
	if (parsed && isBookingHost(normalizeHost(parsed.hostname))) return formatBookingUrl(url, username, ctx);

	return stampAffiliateUrl(url);
};

/**
 * Build a direct hotel-page clickout URL: normalizes the affiliate ID via
 * formatPlaceUrl, then appends booking.com check-in/check-out date params.
 * @param url - Direct hotel page URL (typically booking.com)
 * @param username - Current user's username, threaded through to formatPlaceUrl for the
 *   booking.com attribution label.
 * @returns Affiliate-normalized URL with date params when dates are provided
 */
export const buildHotelBookingUrl = (url: string, startDate?: string, endDate?: string, username?: string, ctx?: LabelContext): string => {
	const base = formatPlaceUrl(url, username, ctx);
	if (!base || !startDate || !endDate) return base;
	const s = new Date(startDate);
	const e = new Date(endDate);
	const sep = base.includes("?") ? "&" : "?";
	return `${base}${sep}checkin_year=${s.getFullYear()}&checkin_month=${s.getMonth() + 1}&checkin_monthday=${s.getDate()}&checkout_year=${e.getFullYear()}&checkout_month=${e.getMonth() + 1}&checkout_monthday=${e.getDate()}`;
};

/**
 * True only for a Booking.com *hotel page* — the one shape safe to hand a user as a clickout.
 * A stored place URL can be anything a user pasted: their own confirmation page
 * (`secure.booking.com/confirmation.html?pincode=…`), an account link, or the hotel's own site.
 * Those either leak someone else's booking or earn nothing, so anything that isn't `/hotel/…`
 * falls back to a fresh search. Mirrors stippl-app/src/utils/urlUtils.ts.
 */
export const isBookingHotelPageUrl = (url?: string | null): boolean => !!url && url.includes("booking.com") && url.includes("/hotel/");

/**
 * Which stored link a stay's Booking.com action should be built from, the same on every surface:
 *  1. the stay's own `url` when it points to another booking partner (Airbnb, Expedia, Hostelworld, …) — kept as is;
 *  2. `bookingComUrl`, the server's cached Booking.com page for that exact hotel — also over the hotel's own website,
 *     which is not where a "Book" action should land when we know the hotel's Booking.com page;
 *  3. otherwise the stay's own `url` as today (its Booking.com hotel page, its website, …);
 *  4. nothing — callers then search on hotel name + city (see buildStayClickoutUrl).
 * The result is a bare URL: dates, our aid and the label are stamped by buildStayClickoutUrl / buildHotelBookingUrl.
 */
// Booking partners whose own link outranks our Booking.com page. Same list as stippl-app's stayBookingRow.ts PARTNER_HOSTS.
const OTHER_BOOKING_PARTNER_DOMAINS = ["airbnb.com", "abnb.me", "expedia.com", "hostelworld.com", "agoda.com", "hotels.com", "trip.com", "vio.com"];
const AIRBNB_OR_EXPEDIA_HOST = /(^|\.)(airbnb|expedia)\.[a-z]+(\.[a-z]+)?$/;
const isOtherBookingPartnerUrl = (url: string): boolean => {
	const parsed = parseUrlOrNull(url);
	if (!parsed) return false;
	const host = normalizeHost(parsed.hostname);
	return AIRBNB_OR_EXPEDIA_HOST.test(host) || OTHER_BOOKING_PARTNER_DOMAINS.some((domain) => isSameOrSubdomain(host, domain));
};

export const pickStayClickoutUrl = ({ url, bookingComUrl }: { url?: string | null; bookingComUrl?: string | null }): string | undefined => {
	if (url && isOtherBookingPartnerUrl(url)) return url;
	if (bookingComUrl && isBookingHotelPageUrl(bookingComUrl)) return bookingComUrl;
	return url || undefined;
};

/**
 * The URL a "Book" action on an in-plan stay should open.
 *
 * The per-stay fallback used to be `getPartnerUrl("Booking.com", dest.city, …)`, which searches on
 * **city alone** — dropping the user on a results page for a hotel they had already chosen, which
 * is exactly the friction that sends them to book.com directly instead of through us. Only 13% of
 * in-plan accommodations carry a stored booking.com URL (529 of 3,989, July 2026), so this was the
 * common path, not the edge case.
 *
 * **Always returns a URL carrying our affiliate id.** Even with no name and no city we send the user
 * to Booking.com tagged — the aid is what earns, and Booking.com attributes on the click, so an
 * untargeted tagged landing still commissions whatever they book in that session.
 */
export const buildStayClickoutUrl = ({
	url,
	name,
	city,
	startDate,
	endDate,
	username,
	ctx,
}: {
	url?: string;
	name?: string;
	city?: string;
	startDate?: string;
	endDate?: string;
	username?: string;
	ctx?: LabelContext;
}): string => {
	// Non-Booking partners (GetYourGuide, Viator, …) keep today's behaviour untouched.
	if (url && !url.includes("booking.com")) return formatPlaceUrl(url, username, ctx);

	if (isBookingHotelPageUrl(url)) return buildHotelBookingUrl(url as string, startDate, endDate, username, ctx);

	// Search on hotel NAME + city, not city alone.
	const query = [name, city].filter(Boolean).join(" ").trim();

	// No query still gets a tagged landing rather than nothing — the aid is what earns.
	const base = query
		? `https://www.booking.com/searchresults.en-us.html?aid=1787423&ss=${encodeURIComponent(query)}`
		: "https://www.booking.com/index.html?aid=1787423";
	const withDates =
		startDate && endDate
			? (() => {
					const s = new Date(startDate);
					const e = new Date(endDate);
					return `${base}&checkin_year=${s.getFullYear()}&checkin_month=${s.getMonth() + 1}&checkin_monthday=${s.getDate()}&checkout_year=${e.getFullYear()}&checkout_month=${e.getMonth() + 1}&checkout_monthday=${e.getDate()}`;
				})()
			: base;
	const label = buildAttributionLabel(username, ctx);
	return label ? `${withDates}&label=${encodeURIComponent(label)}` : withDates;
};
